/**
 * Sets a cookie.
 * @param {string} name - Name for the cookie.
 * @param {string} value - Value for the cookie.
 * @param {number} days - Number of days until the cookie should expire.
 */
function setCookie(name, value, days) {
  let expires = '';

  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    expires = `; expires=${date.toUTCString()}`;
  }

  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}${expires}; path=/; SameSite=None; Secure`;
}

/**
 * Gets the value of a cookie (if it exists).
 * @param {string} name - Name for the cookie.
 * @returns {?string}
 */
function getCookie(name) {
  const cookieString = `; ${document.cookie}`;
  const cookies = cookieString.split(`; ${name}=`);

  if (cookies.length === 2) {
    return cookies.pop().split(';').shift();
  }

  return null;
}

/* global Modal */

if (!customElements.get('pop-up')) {
  customElements.whenDefined('modal-dialog').then(() => {
    class PopUp extends Modal {
      constructor() {
        super();
        this.cookie = `${this.id}-dismissed`;
        this.autoOpenTimer = null;
        this.countdownTimer = null;
        this.countdown = this.querySelector('[data-popup-countdown]');
        this.countdownValue = this.querySelector('[data-popup-countdown-value]');
        this.countdownMinutes = this.querySelector('[data-popup-countdown-minutes]');
        this.countdownSeconds = this.querySelector('[data-popup-countdown-seconds]');
        this.countdownAnimation = null;
        this.keepOpenButton = this.querySelector('[data-popup-keep-open]');
        this.keepOpenHandler = () => {
          this.pauseCountdown();
          const input = this.querySelector('input[type="email"]');
          if (input) input.focus();
        };
        if (this.keepOpenButton) this.keepOpenButton.addEventListener('click', this.keepOpenHandler);
        this.formInteractionHandler = (evt) => {
          if (evt.target instanceof Element && evt.target.closest('.signup-form')) this.pauseCountdown();
        };
        this.addEventListener('focusin', this.formInteractionHandler);
        this.addEventListener('input', this.formInteractionHandler);
        this.addEventListener('change', this.formInteractionHandler);
        this.addEventListener('submit', this.formInteractionHandler);
        this.addEventListener('invalid', this.formInteractionHandler, true);

        this.offerTriggerHandler = (evt) => {
          const target = evt.target instanceof Element ? evt.target.closest('a[href="#elookus-welcome-offer"]') : null;
          if (!target) return;
          evt.preventDefault();
          this.open(target);
        };
        document.addEventListener('click', this.offerTriggerHandler);

        if (Shopify.designMode) {
          document.addEventListener('shopify:section:select', (evt) => {
            if (evt.target === this.closest('.shopify-section')) this.open();
          });

          document.addEventListener('shopify:section:deselect', this.close.bind(this));
        } else if (!getCookie(this.cookie) && (this.dataset.showOnMobile === 'true'
          || theme.mediaMatches.md)) {
          if (this.querySelector('.alert')) {
            this.open();
          } else if (this.dataset.trigger === 'delay') {
            this.autoOpenTimer = setTimeout(() => {
              if (!getCookie(this.cookie)) this.open();
            }, Number(this.dataset.delay) * 1000);
          } else if (this.dataset.trigger === 'exit' && theme.mediaMatches.md) {
            this.mouseLeaveHandler = this.mouseLeaveHandler || this.handleMouseLeave.bind(this);
            document.body.addEventListener('mouseleave', this.mouseLeaveHandler);
          }
        }
      }

      /**
       * Triggers when the web component is removed from the DOM
       */
      disconnectedCallback() {
        clearTimeout(this.autoOpenTimer);
        this.stopCountdown();
        if (this.keepOpenButton) this.keepOpenButton.removeEventListener('click', this.keepOpenHandler);
        this.removeEventListener('focusin', this.formInteractionHandler);
        this.removeEventListener('input', this.formInteractionHandler);
        this.removeEventListener('change', this.formInteractionHandler);
        this.removeEventListener('submit', this.formInteractionHandler);
        this.removeEventListener('invalid', this.formInteractionHandler, true);
        if (this.mouseLeaveHandler) {
          document.body.removeEventListener('mouseleave', this.mouseLeaveHandler);
        }
        if (this.offerTriggerHandler) {
          document.removeEventListener('click', this.offerTriggerHandler);
        }
      }

      /**
       * Handles 'mouseleave' events on the body.
       * @param {object} evt - Event object.
       */
      handleMouseLeave(evt) {
        if (evt.clientY < 0 && !getCookie(this.cookie)) this.open();
      }

      open(opener) {
        clearTimeout(this.autoOpenTimer);
        if (this.hasAttribute('open')) return;
        super.open(opener);
        const dialog = this.querySelector('.modal__window');
        if (dialog) dialog.focus({ preventScroll: true });
        this.startCountdown();
      }

      startCountdown() {
        this.stopCountdown();
        if (!this.countdown || !this.countdownValue) return;
        this.countdown.hidden = false;
        if (this.querySelector('.alert, [data-elookus-welcome-signup-error], [data-elookus-welcome-signup-success]')) {
          this.pauseCountdown();
          return;
        }
        this.countdown.querySelector('[data-popup-countdown-active]').hidden = false;
        this.countdown.querySelector('[data-popup-countdown-paused]').hidden = true;
        if (this.keepOpenButton) this.keepOpenButton.hidden = false;
        this.countdownDeadline = Date.now() + 60000;
        this.updateCountdown();
      }

      updateCountdown() {
        if (!this.hasAttribute('open') || !this.countdownDeadline) return;
        const remaining = Math.max(0, Math.ceil((this.countdownDeadline - Date.now()) / 1000));
        const minutes = Math.floor(remaining / 60).toString().padStart(2, '0');
        const seconds = (remaining % 60).toString().padStart(2, '0');
        this.countdownValue.textContent = `${minutes}:${seconds}`;
        if (this.countdownMinutes) this.countdownMinutes.textContent = minutes;
        if (this.countdownSeconds) {
          const changed = this.countdownSeconds.textContent !== seconds;
          this.countdownSeconds.textContent = seconds;
          if (this.countdownAnimation) this.countdownAnimation.cancel();
          if (changed && remaining > 0 && this.countdownSeconds.animate
            && typeof window !== 'undefined' && window.matchMedia
            && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            this.countdownAnimation = this.countdownSeconds.animate(
              [{ transform: 'translateY(4px)', opacity: 0.65 }, { transform: 'translateY(0)', opacity: 1 }],
              { duration: 180, easing: 'ease-out' }
            );
          }
        }
        if (remaining === 0) {
          this.close();
          return;
        }
        this.countdownTimer = setTimeout(() => this.updateCountdown(), 1000);
      }

      stopCountdown() {
        if (this.countdownAnimation) this.countdownAnimation.cancel();
        this.countdownAnimation = null;
        clearTimeout(this.countdownTimer);
        this.countdownTimer = null;
        this.countdownDeadline = null;
      }

      pauseCountdown() {
        this.stopCountdown();
        if (!this.countdown) return;
        this.countdown.querySelector('[data-popup-countdown-active]').hidden = true;
        this.countdown.querySelector('[data-popup-countdown-paused]').hidden = false;
        if (this.keepOpenButton) this.keepOpenButton.hidden = true;
      }

      close() {
        clearTimeout(this.autoOpenTimer);
        this.stopCountdown();
        super.close();
        setCookie(this.cookie, true, parseInt(this.dataset.dismissDays, 10));
      }
    }

    customElements.define('pop-up', PopUp);
  });
}
